// Push Notification Service for OnsiteHand
// Handles Web Push subscription and management

import { BACKEND_URL } from '../config/api';

// Development mode check for logging
const isDev = process.env.NODE_ENV === 'development';
const log = (...args) => isDev && console.log('[Push]', ...args);

// ==================== UTILITY FUNCTIONS ====================

/**
 * Convert VAPID public key from base64url to Uint8Array
 * Required for PushManager.subscribe()
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Convert ArrayBuffer to base64url string (for sending to backend)
 */
function arrayBufferToBase64Url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

// ==================== SUPPORT CHECKS ====================

/**
 * Check if push notifications are supported in this browser
 */
export function isPushSupported() {
  return (
    'serviceWorker' in navigator && 
    'PushManager' in window && 
    'Notification' in window
  );
}

/**
 * Check if running as installed PWA
 */
export function isInstalledPWA() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

/**
 * Get notification permission status
 * @returns {'granted' | 'denied' | 'default' | 'unsupported'}
 */
export function getNotificationPermission() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

// ==================== SERVICE WORKER ====================

/**
 * Register the service worker
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export async function registerServiceWorker() {
  if (!isPushSupported()) {
    console.warn('[Push] Push notifications not supported');
    return null;
  }
  
  try {
    // Register service worker
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    });
    
    log(' Service Worker registered:', registration.scope);
    
    // Wait for activation
    if (registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    
    return registration;
  } catch (error) {
    console.error('[Push] Service Worker registration failed:', error);
    return null;
  }
}

/**
 * Get the active service worker registration
 * @returns {Promise<ServiceWorkerRegistration>}
 */
export async function getServiceWorkerRegistration() {
  if (!isPushSupported()) {
    throw new Error('Push notifications not supported');
  }
  return navigator.serviceWorker.ready;
}

// ==================== PERMISSION ====================

/**
 * Request notification permission from the user
 * @returns {Promise<{success: boolean, permission: string, error?: string}>}
 */
export async function requestNotificationPermission() {
  if (!isPushSupported()) {
    return { 
      success: false, 
      permission: 'unsupported',
      error: 'Push notifications are not supported in this browser' 
    };
  }
  
  try {
    const permission = await Notification.requestPermission();
    log(' Permission result:', permission);
    
    return { 
      success: permission === 'granted', 
      permission 
    };
  } catch (error) {
    console.error('[Push] Permission request failed:', error);
    return { 
      success: false, 
      permission: 'error',
      error: error.message 
    };
  }
}

// ==================== SUBSCRIPTION ====================

/**
 * Get VAPID public key from backend
 * @returns {Promise<string|null>}
 */
async function getVapidPublicKey() {
  try {
    const response = await fetch(`${BACKEND_URL}/api/push/vapid-public-key`, {
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch VAPID key');
    }
    
    const data = await response.json();
    
    if (!data.publicKey) {
      throw new Error('VAPID public key not configured on server');
    }
    
    return data.publicKey;
  } catch (error) {
    console.error('[Push] Failed to get VAPID key:', error);
    return null;
  }
}

/**
 * Subscribe to push notifications
 * This will:
 * 1. Request notification permission
 * 2. Get service worker registration
 * 3. Subscribe to push manager
 * 4. Send subscription to backend
 * 
 * @returns {Promise<{success: boolean, subscription?: PushSubscription, error?: string}>}
 */
export async function subscribeToPush() {
  if (!isPushSupported()) {
    return { 
      success: false, 
      error: 'Push notifications are not supported in this browser' 
    };
  }
  
  try {
    // Step 1: Request permission
    const permissionResult = await requestNotificationPermission();
    if (!permissionResult.success) {
      return {
        success: false,
        error: permissionResult.permission === 'denied' 
          ? 'Notification permission was denied. Please enable it in browser settings.'
          : 'Failed to get notification permission'
      };
    }
    
    // Step 2: Get service worker registration
    const registration = await navigator.serviceWorker.ready;
    log(' Service Worker ready');
    
    // Step 3: Get VAPID public key
    const vapidPublicKey = await getVapidPublicKey();
    if (!vapidPublicKey) {
      return { success: false, error: 'Push notifications are not configured on the server' };
    }
    
    // Step 4: Subscribe to push manager
    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
    
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey
    });
    
    log(' Subscribed to push manager');
    
    // Step 5: Send subscription to backend
    const subscriptionData = {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: arrayBufferToBase64Url(subscription.getKey('p256dh')),
        auth: arrayBufferToBase64Url(subscription.getKey('auth'))
      },
      expirationTime: subscription.expirationTime
    };
    
    const response = await fetch(`${BACKEND_URL}/api/push/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(subscriptionData)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to save subscription to server');
    }
    
    const result = await response.json();
    log(' Subscription saved:', result);
    
    return { 
      success: true, 
      subscription,
      subscriptionId: result.subscription_id
    };
    
  } catch (error) {
    console.error('[Push] Subscription error:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to subscribe to push notifications'
    };
  }
}

/**
 * Unsubscribe from push notifications
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function unsubscribeFromPush() {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      // Unsubscribe from push manager
      await subscription.unsubscribe();
      log(' Unsubscribed from push manager');
      
      // Remove from backend
      const response = await fetch(
        `${BACKEND_URL}/api/push/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );
      
      if (!response.ok) {
        console.warn('[Push] Backend unsubscribe failed, but local unsubscribe succeeded');
      }
    }
    
    return { success: true };
  } catch (error) {
    console.error('[Push] Unsubscribe error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Check if user is currently subscribed to push notifications
 * @returns {Promise<boolean>}
 */
export async function isSubscribedToPush() {
  if (!isPushSupported()) return false;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription !== null;
  } catch (error) {
    console.error('[Push] Check subscription error:', error);
    return false;
  }
}

/**
 * Get current push subscription
 * @returns {Promise<PushSubscription|null>}
 */
export async function getCurrentSubscription() {
  if (!isPushSupported()) return null;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  } catch (error) {
    console.error('[Push] Get subscription error:', error);
    return null;
  }
}

// ==================== TESTING ====================

/**
 * Send a test push notification to the current user
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export async function sendTestNotification() {
  try {
    const response = await fetch(`${BACKEND_URL}/api/push/test`, {
      method: 'POST',
      credentials: 'include'
    });
    
    const data = await response.json();
    
    if (response.ok && data.delivered_to > 0) {
      return { 
        success: true, 
        message: `Test notification sent to ${data.delivered_to} device(s)`
      };
    }
    
    return { 
      success: false, 
      error: data.reason || 'No subscriptions found'
    };
  } catch (error) {
    console.error('[Push] Test notification error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Show a local notification (for testing without backend)
 * @param {string} title 
 * @param {string} body 
 */
export async function showLocalNotification(title, body) {
  if (!isPushSupported()) return;
  
  const permission = Notification.permission;
  if (permission !== 'granted') return;
  
  const registration = await navigator.serviceWorker.ready;
  await registration.showNotification(title, {
    body,
    icon: '/icons/icon-192.png',
    badge: '/icons/icon-72.png',
    vibrate: [200, 100, 200]
  });
}

// ==================== INITIALIZATION ====================

/**
 * Initialize push notifications
 * Call this early in your app (e.g., App.js or after login)
 * @returns {Promise<{supported: boolean, registered: boolean, subscribed: boolean}>}
 */
export async function initializePushNotifications() {
  const result = {
    supported: isPushSupported(),
    registered: false,
    subscribed: false,
    permission: getNotificationPermission()
  };
  
  if (!result.supported) {
    log(' Push notifications not supported');
    return result;
  }
  
  // Register service worker
  const registration = await registerServiceWorker();
  result.registered = registration !== null;
  
  if (result.registered) {
    // Check if already subscribed
    result.subscribed = await isSubscribedToPush();
    log(' Initialized:', result);
  }
  
  return result;
}

export default {
  isPushSupported,
  isInstalledPWA,
  getNotificationPermission,
  registerServiceWorker,
  requestNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribedToPush,
  getCurrentSubscription,
  sendTestNotification,
  showLocalNotification,
  initializePushNotifications
};
